import { useCallback, useEffect, useState } from 'react';
import { endOfDay, format, startOfDay, subDays } from 'date-fns';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import {
  fetchStandaloneInvoiceSales,
  sumStandaloneInvoiceSales,
} from '../lib/invoices/standaloneInvoiceSales';

type BookingRow = {
  subtotal: number | null;
  discount_amount: number | null;
};

function sumSales(rows: BookingRow[] | null) {
  return (rows ?? []).reduce((acc, booking) => {
    const subtotal = booking.subtotal ?? 0;
    const discount = booking.discount_amount ?? 0;
    return acc + (subtotal - discount);
  }, 0);
}

/**
 * Today's sales on both axes: created today (booked) and finishing today
 * (jobs whose end_date is today — the delivery basis the monthly tile uses).
 */
export function useSalesCreatedTodayMetrics() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;

  const [salesCreatedToday, setSalesCreatedToday] = useState(0);
  const [salesCreatedYesterday, setSalesCreatedYesterday] = useState(0);
  const [salesFinishingToday, setSalesFinishingToday] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setSalesCreatedToday(0);
      setSalesCreatedYesterday(0);
      setSalesFinishingToday(0);
      setLoading(false);
      return;
    }

    const bookingFilters = (query: ReturnType<typeof supabase.from>) =>
      query
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .neq('status', 'cancelled')
        .or('in_que.is.null,in_que.eq.false');

    try {
      const now = new Date();
      const createdTodayStart = startOfDay(now).toISOString();
      const createdTodayEnd = endOfDay(now).toISOString();
      const yesterday = subDays(now, 1);
      const createdYesterdayStart = startOfDay(yesterday).toISOString();
      const createdYesterdayEnd = endOfDay(yesterday).toISOString();

      const todayKey = format(now, 'yyyy-MM-dd');

      const [createdTodayResult, createdYesterdayResult, endingTodayResult, standaloneToday, standaloneYesterday] = await Promise.all([
        bookingFilters(supabase.from('bookings').select('subtotal, discount_amount'))
          .gte('created_at', createdTodayStart)
          .lte('created_at', createdTodayEnd),
        bookingFilters(supabase.from('bookings').select('subtotal, discount_amount'))
          .gte('created_at', createdYesterdayStart)
          .lte('created_at', createdYesterdayEnd),
        // Jobs finishing today — same end_date basis as the monthly tile total.
        bookingFilters(supabase.from('bookings').select('subtotal, discount_amount'))
          .gte('end_date', todayKey)
          .lte('end_date', todayKey),
        // Standalone invoices ISSUED that day count as sales created.
        fetchStandaloneInvoiceSales(businessId, { start: createdTodayStart, end: createdTodayEnd }),
        fetchStandaloneInvoiceSales(businessId, { start: createdYesterdayStart, end: createdYesterdayEnd }),
      ]);

      if (createdTodayResult.error) {
        console.error('Error fetching sales created today:', createdTodayResult.error);
        setSalesCreatedToday(0);
      } else {
        setSalesCreatedToday(sumSales(createdTodayResult.data) + sumStandaloneInvoiceSales(standaloneToday));
      }

      if (createdYesterdayResult.error) {
        console.error('Error fetching sales created yesterday:', createdYesterdayResult.error);
        setSalesCreatedYesterday(0);
      } else {
        setSalesCreatedYesterday(sumSales(createdYesterdayResult.data) + sumStandaloneInvoiceSales(standaloneYesterday));
      }

      if (endingTodayResult.error) {
        console.error('Error fetching sales finishing today:', endingTodayResult.error);
        setSalesFinishingToday(0);
      } else {
        // Invoices issued today are booked and delivered the same day.
        setSalesFinishingToday(sumSales(endingTodayResult.data) + sumStandaloneInvoiceSales(standaloneToday));
      }
    } catch (err) {
      console.error('Error loading sales created today metrics:', err);
      setSalesCreatedToday(0);
      setSalesCreatedYesterday(0);
      setSalesFinishingToday(0);
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId]);

  useEffect(() => {
    setLoading(true);
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  return { salesCreatedToday, salesCreatedYesterday, salesFinishingToday, loading, refresh: load };
}
